import { useDeferredValue, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api, ErreurApi, type Compte } from "../api/client";
import {
  Chargement,
  EnTetePage,
  EtiquetteSens,
  JetonsDeCompte,
  Message,
  Vide,
} from "../composants/Elements";
import { formaterNombre } from "../domaine/formats";

/**
 * Plan de comptes du référentiel en vigueur.
 *
 * La hiérarchie se lit sur le numéro lui-même : le parent de 3421 est 342. Le
 * retrait la rend visible, et le tableau reste plat — donc filtrable et
 * cherchable sans déplier quoi que ce soit.
 */
export function PagePlanComptable() {
  const { code = "PCGM" } = useParams();
  const [recherche, setRecherche] = useState("");
  const [classe, setClasse] = useState<number | undefined>(undefined);

  // La frappe reste fluide même quand la liste est longue : le rendu du
  // tableau suit la saisie sans la retenir.
  const rechercheDifferee = useDeferredValue(recherche);

  const referentiel = useQuery({
    queryKey: ["referentiel", code],
    queryFn: () => api.referentiel(code),
  });

  const comptes = useQuery({
    queryKey: ["comptes", code, rechercheDifferee, classe],
    queryFn: () => api.comptes(code, { recherche: rechercheDifferee || undefined, classe }),
  });

  const erreur = referentiel.error ?? comptes.error;

  if (erreur != null) {
    return (
      <>
        <EnTetePage titre={`Plan comptable — ${code}`} />
        <Message type="erreur" titre={erreur instanceof ErreurApi ? erreur.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{erreur.message}</p>
        </Message>
      </>
    );
  }

  return (
    <>
      <EnTetePage
        titre={`Plan comptable — ${code}`}
        description={
          referentiel.data?.libelle ??
          "La hiérarchie du plan est revérifiée à chaque lecture : un compte dont le parent manque fait échouer le chargement, et non un calcul d'état trois écrans plus loin."
        }
      />

      <div className="filtres-plan">
        <input
          type="search"
          value={recherche}
          onChange={(evenement) => setRecherche(evenement.target.value)}
          placeholder="Numéro ou libellé — 4411, fournisseurs, TVA…"
          aria-label="Rechercher un compte"
        />

        <select
          value={classe ?? ""}
          onChange={(evenement) =>
            setClasse(evenement.target.value === "" ? undefined : Number(evenement.target.value))
          }
          aria-label="Filtrer par classe"
        >
          <option value="">Toutes les classes</option>
          {referentiel.data?.classes.map((c) => (
            <option key={c.chiffre} value={c.chiffre}>
              {c.chiffre} — {c.libelle}
            </option>
          ))}
        </select>

        <span className="decompte">
          {comptes.data != null
            ? `${formaterNombre(comptes.data.length)} compte${comptes.data.length > 1 ? "s" : ""}`
            : ""}
        </span>
      </div>

      {comptes.isPending && <Chargement quoi="le plan de comptes" />}

      {comptes.data != null && comptes.data.length === 0 && (
        <Vide>Aucun compte ne correspond à ce filtre.</Vide>
      )}

      {comptes.data != null && comptes.data.length > 0 && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Libellé</th>
                <th>Sens normal</th>
                <th>Caractéristiques</th>
              </tr>
            </thead>
            <tbody>
              {comptes.data.map((compte) => (
                <LigneCompte key={compte.numero} compte={compte} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function LigneCompte({ compte }: { compte: Compte }) {
  return (
    <tr className={compte.mouvementable ? undefined : "agregation"}>
      <td className="numero-compte">{compte.numero}</td>
      <td className="libelle-compte" data-niveau={Math.min(compte.niveau, 5)}>
        {compte.libelle}
      </td>
      <td>
        <EtiquetteSens sens={compte.sensNormal} />
      </td>
      <td>
        <JetonsDeCompte compte={compte} />
      </td>
    </tr>
  );
}
